import { getSrhtExamGradeLabelFromPoints } from './srhtExamFinalEvaluation.js';
import {
  AKTIVRETTUNG_GRADE_RANGES,
  AKTIVRETTUNG_MAX_POINTS,
  AKTIVRETTUNG_SECTION_DEFINITIONS,
  AKTIVRETTUNG_SECTION_MAX_POINTS,
  AKTIVRETTUNG_TIME_MAX_POINTS,
  AKTIVRETTUNG_TIME_POINT_RULES,
  EINZELUEBERPRUEFUNG_GRADE_RANGES,
  EINZELUEBERPRUEFUNG_SECTION_DEFINITIONS,
  EINZELUEBERPRUEFUNG_TIME_POINT_RULES,
  FALLSCHUTZ_GRADE_RANGES,
  FALLSCHUTZ_TIME_POINT_RULES,
  GRUPPENPRUEFUNG_GRADE_RANGES,
  GRUPPENPRUEFUNG_PARTICIPANT_POSITIONS,
  GRUPPENPRUEFUNG_TIME_POINT_RULES,
  SRHT_EXAM_STATION_AKTIVRETTUNG,
  SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG,
  SRHT_EXAM_STATION_FALLSCHUTZ,
  SRHT_EXAM_STATION_FITNESS_COORDINATION,
  SRHT_EXAM_STATION_GRUPPENPRUEFUNG,
  formatDuration,
  getAktivrettungGrade,
  getAktivrettungOverallPoints,
  getEffectiveFitnessCoordinationTotalMs,
  getEinzelueberpruefungGrade,
  getEinzelueberpruefungOverallPoints,
  getFitnessCoordinationGrade,
  getGroupPracticalGrade,
  getGroupPracticalOverallPoints,
  getGroupPracticalTimePoints,
  getGruppenpruefungActionAverage,
  getGruppenpruefungActionScoresByAction,
  getGruppenpruefungAverageTimeMs,
} from './srhtExamPracticalSheets.js';

const WRITTEN_MAX_POINTS = 129;
const EINZELUEBERPRUEFUNG_SECTION_MAX_POINTS = 110;

const PRACTICAL_STATION_TITLES = {
  [SRHT_EXAM_STATION_FITNESS_COORDINATION]: 'Prüfbogen 1 Fitness- & Koordinationsprüfung',
  [SRHT_EXAM_STATION_AKTIVRETTUNG]: 'Prüfbogen 2 Aktivrettung',
  [SRHT_EXAM_STATION_FALLSCHUTZ]: 'Prüfbogen 3 Person im Fallschutz',
  [SRHT_EXAM_STATION_GRUPPENPRUEFUNG]: 'Prüfbogen 4 Tragenrettung',
  [SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG]: 'Prüfbogen 5 Einzelüberprüfung',
};

const REPORT_DISCIPLINE_TITLES = {
  written_exam: 'Schriftliche Prüfung',
  [SRHT_EXAM_STATION_FITNESS_COORDINATION]: 'Fitness & Koordination',
  [SRHT_EXAM_STATION_AKTIVRETTUNG]: 'Kameradenrettung',
  [SRHT_EXAM_STATION_FALLSCHUTZ]: 'Person im Fallschutz',
  [SRHT_EXAM_STATION_GRUPPENPRUEFUNG]: 'Tragenrettung',
  [SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG]: 'Einzelüberprüfung',
};

const REPORT_DISCIPLINE_ORDER = [
  'written_exam',
  SRHT_EXAM_STATION_FITNESS_COORDINATION,
  SRHT_EXAM_STATION_AKTIVRETTUNG,
  SRHT_EXAM_STATION_FALLSCHUTZ,
  SRHT_EXAM_STATION_GRUPPENPRUEFUNG,
  SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG,
];

const FITNESS_GRADE_RULES = [
  { label: 'Sehr gut', value: '< 05:30 min' },
  { label: 'Gut', value: '05:30 min bis 06:30 min' },
  { label: 'Befriedigend', value: '06:31 min bis 09:00 min' },
  { label: 'Ausreichend', value: '09:01 min bis 11:00 min' },
  { label: 'Mangelhaft', value: '11:01 min bis 14:00 min' },
  { label: 'Ungenügend', value: '> 14:00 min' },
];

function roundToTwoDecimals(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }
  return Math.round(numericValue * 100) / 100;
}

function formatDate(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatNumber(value, digits = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return Number(numeric.toFixed(digits)).toLocaleString('de-DE');
}

function formatWholeNumber(value, rounding = 'round') {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  if (rounding === 'ceil') {
    return String(Math.ceil(numeric));
  }
  if (rounding === 'floor') {
    return String(Math.floor(numeric));
  }
  return String(Math.round(numeric));
}

function formatGradeRuleValue(rule = {}) {
  if (rule?.value) {
    return String(rule.value);
  }

  const minValue = formatWholeNumber(rule?.min, 'ceil');
  const maxValue = formatWholeNumber(rule?.max, 'floor');
  if (minValue === '-' || maxValue === '-') {
    return '-';
  }

  if (minValue === maxValue) {
    return `${minValue} Punkte`;
  }

  return `${minValue} bis ${maxValue} Punkte`;
}

function buildTable(header, rows, widths) {
  return {
    table: {
      headerRows: 1,
      widths,
      body: [
        header.map((item) => ({ text: item, style: 'tableHeader' })),
        ...rows,
      ],
    },
    layout: 'lightHorizontalLines',
    margin: [0, 4, 0, 10],
  };
}

function buildRichKeyValueTable(entries = []) {
  return {
    table: {
      widths: [180, '*'],
      body: entries.map(([label, value]) => ([
        { text: label, style: 'labelCell' },
        Array.isArray(value)
          ? { stack: value.map((item) => (typeof item === 'string' ? { text: item, style: 'valueCell' } : item)) }
          : (value && typeof value === 'object'
            ? value
            : { text: value == null || value === '' ? '-' : String(value), style: 'valueCell' }),
      ])),
    },
    layout: 'lightHorizontalLines',
    margin: [0, 0, 0, 10],
  };
}

function buildPersonNameByUserId(model = {}) {
  const map = new Map();
  const register = (entry) => {
    const userId = Number(entry?.userId || 0);
    if (!Number.isInteger(userId) || userId <= 0 || !entry?.fullName) {
      return;
    }
    map.set(userId, entry.fullName);
  };

  register(model?.staff?.chair);
  register(model?.staff?.courseLeader);
  [
    ...(model?.staff?.examiners || []),
    ...(model?.staff?.personalrat || []),
    ...(model?.staff?.department37_4 || []),
    ...(model?.staff?.equalOpportunityOfficers || []),
    ...(model?.staff?.observers || []),
  ].forEach(register);

  return map;
}

function getPersonName(personNameByUserId, userId, fallback = '-') {
  const normalizedUserId = Number(userId || 0);
  if (!Number.isInteger(normalizedUserId) || normalizedUserId <= 0) {
    return fallback;
  }
  return personNameByUserId.get(normalizedUserId) || fallback;
}

function buildCandidateNameById(model = {}) {
  return new Map((model?.participants || []).map((participant) => [String(participant.candidateId), participant.fullName || '-']));
}

function formatStaffList(entries = [], { emptyLabel = 'Nicht anwesend', includeNotes = false } = {}) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return emptyLabel;
  }

  return entries
    .map((entry) => {
      const fullName = entry?.fullName || '-';
      const noteText = String(entry?.noteText || '').trim();
      return includeNotes && noteText ? `${fullName} (${noteText})` : fullName;
    })
    .join(', ');
}

function getSourceCourseRangeLabel(sourceCourses = []) {
  const validCourses = (Array.isArray(sourceCourses) ? sourceCourses : []).filter((course) => course?.startDate || course?.endDate);
  if (!validCourses.length) {
    return '-';
  }

  const startDate = validCourses.map((course) => String(course.startDate || '').trim()).filter(Boolean).sort()[0] || '';
  const endDate = validCourses.map((course) => String(course.endDate || '').trim()).filter(Boolean).sort().slice(-1)[0] || '';

  return `${formatDate(startDate)} bis ${formatDate(endDate)}`;
}

function formatSafetyIncidentLabel(safetyIncident = {}) {
  if (safetyIncident?.answered !== true) {
    return 'Noch nicht beantwortet';
  }
  return safetyIncident?.hasIncident ? 'Ja' : 'Nein';
}

function formatProtectionStatus(status) {
  return String(status || '').trim() === 'finalized' ? 'Finalisiert' : (String(status || '').trim() || 'draft');
}

function buildProtectionLines(record, personNameByUserId, extraLines = []) {
  const lines = [`Status: ${formatProtectionStatus(record?.status)}`];

  if (record?.finalizedAt) {
    lines.push(`Finalisiert am: ${formatDateTime(record.finalizedAt)}`);
  }
  if (record?.finalizedByUserId) {
    lines.push(`Finalisiert durch: ${getPersonName(personNameByUserId, record.finalizedByUserId)}`);
  }
  if (record?.lastUnlockedAt) {
    lines.push(`Zuletzt entsperrt am: ${formatDateTime(record.lastUnlockedAt)}`);
  }
  if (record?.lastUnlockedByUserId) {
    lines.push(`Entsperrt durch: ${getPersonName(personNameByUserId, record.lastUnlockedByUserId)}`);
  }

  return [...lines, ...extraLines.filter(Boolean)];
}

function getGradePoints(grade) {
  const numericGrade = Number(grade);
  if (numericGrade === 1) return 15;
  if (numericGrade === 2) return 12;
  if (numericGrade === 3) return 9;
  if (numericGrade === 4) return 6;
  if (numericGrade === 5) return 3;
  return 0;
}

function getDisplayDiscipline(discipline = {}) {
  const displayPoints = discipline?.points == null ? 0 : Number(discipline.points || 0);
  return {
    ...discipline,
    title: REPORT_DISCIPLINE_TITLES[discipline.code] || discipline.shortTitle || discipline.title || '-',
    displayPoints,
    displayGradeLabel: getSrhtExamGradeLabelFromPoints(displayPoints),
    rawValueLabel: discipline?.rawValueLabel || 'Noch offen',
  };
}

function buildReportEvaluation(model = {}) {
  const candidates = Array.isArray(model?.finalEvaluation?.candidates) ? model.finalEvaluation.candidates : [];

  return candidates
    .map((candidate) => {
      const disciplines = REPORT_DISCIPLINE_ORDER.map((disciplineCode) => {
        const discipline = (candidate?.baseDisciplines || candidate?.disciplines || []).find((item) => item.code === disciplineCode) || { code: disciplineCode, points: null, rawValueLabel: 'Noch offen' };
        return getDisplayDiscipline(discipline);
      });
      const writtenDiscipline = disciplines.find((discipline) => discipline.code === 'written_exam') || disciplines[0];
      const practicalDisciplines = disciplines.filter((discipline) => discipline.code !== 'written_exam');
      const practicalAverage = roundToTwoDecimals(practicalDisciplines.reduce((sum, discipline) => sum + Number(discipline.displayPoints || 0), 0) / Math.max(practicalDisciplines.length, 1));
      const weightedPractical = roundToTwoDecimals(practicalAverage * 2);
      const overallPointsRaw = roundToTwoDecimals((Number(writtenDiscipline.displayPoints || 0) + weightedPractical) / 3);
      const overallWholePoints = Math.max(0, Math.min(15, Math.round(overallPointsRaw)));
      const percentScore = roundToTwoDecimals((overallPointsRaw / 15) * 100);
      const oralResolved = !candidate?.oralRetake?.eligible || (candidate?.oralRetake?.record && candidate.oralRetake.record.isDraft !== true);
      const resultStatus = !candidate?.complete || !oralResolved ? 'open' : (candidate?.passed ? 'passed' : 'failed');
      const byBest = [...disciplines].sort((left, right) => {
        if (Number(right.displayPoints) !== Number(left.displayPoints)) {
          return Number(right.displayPoints) - Number(left.displayPoints);
        }
        return REPORT_DISCIPLINE_ORDER.indexOf(left.code) - REPORT_DISCIPLINE_ORDER.indexOf(right.code);
      });
      const byWorst = [...disciplines].sort((left, right) => {
        if (Number(left.displayPoints) !== Number(right.displayPoints)) {
          return Number(left.displayPoints) - Number(right.displayPoints);
        }
        return REPORT_DISCIPLINE_ORDER.indexOf(left.code) - REPORT_DISCIPLINE_ORDER.indexOf(right.code);
      });

      return {
        ...candidate,
        disciplines,
        writtenDiscipline,
        practicalAverage,
        weightedPractical,
        overallPointsRaw,
        overallWholePoints,
        percentScore,
        resultStatus,
        reportGradeLabel: getSrhtExamGradeLabelFromPoints(overallWholePoints),
        bestDiscipline: byBest[0] || null,
        weakestDiscipline: byWorst[0] || null,
      };
    })
    .sort((left, right) => {
      if (Number(right.overallPointsRaw) !== Number(left.overallPointsRaw)) {
        return Number(right.overallPointsRaw) - Number(left.overallPointsRaw);
      }
      return String(left.fullName || '').localeCompare(String(right.fullName || ''), 'de');
    })
    .map((candidate, index) => ({
      ...candidate,
      rankPosition: index + 1,
    }));
}

function buildBadge(label, fillColor, textColor = '#FFFFFF') {
  return {
    table: {
      body: [[{
        text: label,
        bold: true,
        color: textColor,
        fillColor,
        border: [false, false, false, false],
        margin: [8, 4, 8, 4],
        alignment: 'center',
      }]],
    },
    layout: 'noBorders',
  };
}

function getResultBadgeConfig(resultStatus) {
  if (resultStatus === 'passed') {
    return { label: 'Bestanden', fillColor: '#2E7D32', textColor: '#FFFFFF' };
  }
  if (resultStatus === 'failed') {
    return { label: 'Nicht bestanden', fillColor: '#C62828', textColor: '#FFFFFF' };
  }
  return { label: 'Noch offen', fillColor: '#F57C00', textColor: '#FFFFFF' };
}

function buildSummaryPill(label, fillColor) {
  return {
    text: label,
    fillColor,
    color: '#1f2d3a',
    margin: [6, 4, 6, 4],
    border: [false, false, false, false],
    fontSize: 9,
  };
}

function buildSummaryTile(discipline = {}) {
  return {
    table: {
      widths: ['*'],
      body: [[{
        stack: [
          { text: discipline.title || '-', bold: true, margin: [0, 0, 0, 3] },
          { text: `${discipline.displayPoints} / 15 Punkte`, margin: [0, 0, 0, 2] },
          { text: discipline.displayGradeLabel || '-', color: Number(discipline.displayPoints || 0) < 5 ? '#B91C1C' : '#334155', margin: [0, 0, 0, 2] },
          { text: discipline.rawValueLabel || 'Noch offen', style: 'mutedText' },
        ],
        margin: [8, 8, 8, 8],
      }]],
    },
    layout: {
      hLineColor: () => '#D6DEE6',
      vLineColor: () => '#D6DEE6',
      hLineWidth: () => 1,
      vLineWidth: () => 1,
    },
  };
}

function getLaneForResult(sheetRecord, result = {}) {
  const laneId = String(result?.laneId || '').trim();
  if (!laneId) {
    return null;
  }
  return (sheetRecord?.rawPayload?.lanes || []).find((lane) => String(lane?.id || '') === laneId) || null;
}

function resolveGroupContext(sheetRecord, candidateId) {
  const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(candidateId)] || null;
  if (!result) {
    return { result: null, group: null, slot: null };
  }

  const groups = Array.isArray(sheetRecord?.rawPayload?.groups) ? sheetRecord.rawPayload.groups : [];
  const group = groups.find((entry) => String(entry?.id || '') === String(result.groupId || ''))
    || groups.find((entry) => Array.isArray(entry?.participants) && entry.participants.some((participant) => String(participant?.candidateId || '') === String(candidateId)))
    || null;
  const slot = group?.participants?.find((participant) => String(participant?.candidateId || '') === String(candidateId)) || null;

  return { result, group, slot };
}

function getGroupPositionLabel(positionId, fallback = '-') {
  if (fallback && fallback !== '-') {
    return fallback;
  }
  return GRUPPENPRUEFUNG_PARTICIPANT_POSITIONS.find((position) => position.id === positionId)?.label || '-';
}

function getRequiredPracticalExamDateLabel(exam = {}) {
  const practicalExamDate = String(exam?.practicalExamDate || '').trim();
  if (!practicalExamDate) {
    const error = new Error('Der PDF-Export erfordert den Tag der praktischen Prüfung. Bitte Datum in der Prüfung pflegen.');
    error.status = 400;
    throw error;
  }

  return formatDate(practicalExamDate);
}

function getReportInfoText(exam = {}) {
  const practicalExamDateLabel = getRequiredPracticalExamDateLabel(exam);
  return `Das Ergebnis wurde den Lehrgangsteilnehmern am ${practicalExamDateLabel} bekannt gegeben. Die Teilnehmer haben eine Teilnahmebescheinigung erhalten oder werden diese über den Dienstweg erhalten. Die Bescheinigungen werden in das PV-Modul eingepflegt. Dieses Dokument ist die Prüfungsniederschrift des Grundlehrgangs "Spezielle Rettung aus Höhen und Tiefen" und ist ohne weitere Unterschrift gültig.`;
}

function buildWrittenRows(participants = []) {
  return participants.map((participant) => ([
    { text: participant.fullName || '-', style: 'valueCell' },
    { text: participant.writtenResult ? `${Number(participant.writtenResult.points || 0)} / ${WRITTEN_MAX_POINTS}` : '-', style: 'valueCell' },
    { text: participant.reportSummary ? participant.reportSummary.writtenDiscipline.displayGradeLabel : '-', style: 'valueCell' },
  ]));
}

function buildFitnessRows(model, participants, personNameByUserId) {
  return participants.map((participant) => {
    const sheetRecord = model?.practicalSheets?.[SRHT_EXAM_STATION_FITNESS_COORDINATION] || null;
    const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(participant.candidateId)] || null;
    const lane = getLaneForResult(sheetRecord, result || {});
    const totalMs = getEffectiveFitnessCoordinationTotalMs(result || {});
    const grade = getFitnessCoordinationGrade(totalMs);
    const timeLines = result ? [
      Number(result.firstIntermediateMs || 0) > 0 ? `Zwischenzeit 1: ${formatDuration(result.firstIntermediateMs)}` : null,
      Number(result.secondIntermediateMs || 0) > 0 ? `Zwischenzeit 2: ${formatDuration(result.secondIntermediateMs)}` : null,
      totalMs > 0 ? `Gesamtzeit: ${formatDuration(totalMs)}` : null,
    ].filter(Boolean) : [];

    return [
      { text: participant.fullName || '-', style: 'valueCell' },
      { stack: timeLines.length ? timeLines.map((line) => ({ text: line, style: 'valueCell' })) : [{ text: '-', style: 'valueCell' }] },
      { text: formatSafetyIncidentLabel(result?.safetyIncident), style: 'valueCell' },
      { text: result ? getSrhtExamGradeLabelFromPoints(getGradePoints(grade)) : '-', style: 'valueCell' },
      { stack: result ? [
        { text: `Prüfer: ${getPersonName(personNameByUserId, result?.examinerUserId || lane?.examinerUserId)}`, style: 'valueCell' },
        { text: `Kontrolleur: ${getPersonName(personNameByUserId, result?.controllerUserId || lane?.controllerUserId)}`, style: 'valueCell' },
      ] : [{ text: '-', style: 'valueCell' }] },
    ];
  });
}

function buildAktivrettungRows(model, participants, personNameByUserId) {
  return participants.map((participant) => {
    const sheetRecord = model?.practicalSheets?.[SRHT_EXAM_STATION_AKTIVRETTUNG] || null;
    const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(participant.candidateId)] || null;
    const lane = getLaneForResult(sheetRecord, result || {});
    const totalPoints = getAktivrettungOverallPoints(result || {});
    const grade = getAktivrettungGrade(totalPoints);

    return [
      { text: participant.fullName || '-', style: 'valueCell' },
      { text: result?.totalMs ? `Gesamtzeit: ${formatDuration(result.totalMs)}` : '-', style: 'valueCell' },
      { text: formatSafetyIncidentLabel(result?.safetyIncident), style: 'valueCell' },
      { text: result ? `${formatNumber(totalPoints)} / ${AKTIVRETTUNG_MAX_POINTS}` : '-', style: 'valueCell' },
      { text: result ? getSrhtExamGradeLabelFromPoints(getGradePoints(grade)) : '-', style: 'valueCell' },
      { stack: result ? [
        { text: `Prüfer: ${getPersonName(personNameByUserId, result?.examinerUserId || lane?.examinerUserId)}`, style: 'valueCell' },
        { text: `Kontrolleur: ${getPersonName(personNameByUserId, result?.controllerUserId || lane?.controllerUserId)}`, style: 'valueCell' },
      ] : [{ text: '-', style: 'valueCell' }] },
    ];
  });
}

function buildGroupRows(model, participants, personNameByUserId, stationCode) {
  return participants.map((participant) => {
    const sheetRecord = model?.practicalSheets?.[stationCode] || null;
    const { result, group, slot } = resolveGroupContext(sheetRecord, participant.candidateId);
    const totalMs = getGruppenpruefungAverageTimeMs(group || {});
    const overallPoints = getGroupPracticalOverallPoints(result || {}, group || {}, stationCode);
    const grade = getGroupPracticalGrade(overallPoints, stationCode);

    return [
      { text: participant.fullName || '-', style: 'valueCell' },
      { stack: result ? [
        { text: totalMs ? `Gesamtzeit: ${formatDuration(totalMs)}` : '-', style: 'valueCell' },
        { text: totalMs ? `Gewertete Zeit: ${formatDuration(totalMs)}` : '-', style: 'valueCell' },
      ] : [{ text: '-', style: 'valueCell' }] },
      { text: formatSafetyIncidentLabel(group?.safetyIncident), style: 'valueCell' },
      { text: result && overallPoints != null ? `${formatNumber(overallPoints)} / 40` : '-', style: 'valueCell' },
      { text: result ? getSrhtExamGradeLabelFromPoints(getGradePoints(grade)) : '-', style: 'valueCell' },
      { stack: result ? [
        { text: `Prüfer: ${getPersonName(personNameByUserId, slot?.primaryExaminerUserId)}`, style: 'valueCell' },
        { text: `Gruppenführer: ${getPersonName(personNameByUserId, group?.leaderExaminerUserId)}`, style: 'valueCell' },
      ] : [{ text: '-', style: 'valueCell' }] },
    ];
  });
}

function buildEinzelRows(model, participants, personNameByUserId) {
  return participants.map((participant) => {
    const sheetRecord = model?.practicalSheets?.[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG] || null;
    const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(participant.candidateId)] || null;
    const lane = getLaneForResult(sheetRecord, result || {});
    const overallPoints = getEinzelueberpruefungOverallPoints(result || {});
    const grade = getEinzelueberpruefungGrade(overallPoints);

    return [
      { text: participant.fullName || '-', style: 'valueCell' },
      { text: result?.totalMs ? `Gesamtzeit: ${formatDuration(result.totalMs)}` : '-', style: 'valueCell' },
      { text: formatSafetyIncidentLabel(result?.safetyIncident), style: 'valueCell' },
      { text: result ? `${formatNumber(overallPoints)} / 120` : '-', style: 'valueCell' },
      { text: result ? getSrhtExamGradeLabelFromPoints(getGradePoints(grade)) : '-', style: 'valueCell' },
      { stack: result ? [
        { text: `Prüfer: ${getPersonName(personNameByUserId, result?.examinerUserId || lane?.examinerUserId)}`, style: 'valueCell' },
        { text: `Kontrolleur: ${getPersonName(personNameByUserId, result?.controllerUserId || lane?.controllerUserId)}`, style: 'valueCell' },
      ] : [{ text: '-', style: 'valueCell' }] },
    ];
  });
}

function buildRatingKeyBlock(title, gradeRules, timeRules, timeNote = null) {
  return [
    { text: title, style: 'detailSubHeader' },
    { text: 'Notenschlüssel', style: 'subHeader' },
    ...gradeRules.map((rule) => ({ text: `${rule.label}: ${formatGradeRuleValue(rule)}`, style: 'valueText' })),
    { text: 'Bewertungszeit', style: 'subHeader' },
    ...(timeNote ? [{ text: timeNote, style: 'valueText' }] : []),
    ...timeRules.map((rule) => ({ text: `${rule.label}: ${formatWholeNumber(rule.points)} Punkte`, style: 'valueText' })),
  ];
}

function buildWrittenDetail(participant, personNameByUserId) {
  const writtenResult = participant?.writtenResult || null;
  if (!writtenResult) {
    return [
      { text: 'Schriftliche Prüfung', style: 'detailSectionHeader' },
      { text: 'Kein Ergebnis erfasst.', style: 'mutedText', margin: [0, 0, 0, 10] },
    ];
  }

  return [
    { text: 'Schriftliche Prüfung', style: 'detailSectionHeader' },
    buildRichKeyValueTable([
      ['Erreichte Punkte / Maximalpunkte', `${Number(writtenResult.points || 0)} / ${WRITTEN_MAX_POINTS}`],
      ['Note', getSrhtExamGradeLabelFromPoints(getGradePoints(writtenResult.grade))],
      ['Bearbeitungsschutz', buildProtectionLines(writtenResult, personNameByUserId)],
    ]),
  ];
}

function buildFitnessDetail(participant, model, personNameByUserId) {
  const sheetRecord = model?.practicalSheets?.[SRHT_EXAM_STATION_FITNESS_COORDINATION] || null;
  const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(participant.candidateId)] || null;
  if (!result) {
    return [
      { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_FITNESS_COORDINATION], style: 'detailSectionHeader' },
      { text: 'Kein Ergebnis erfasst.', style: 'mutedText', margin: [0, 0, 0, 10] },
    ];
  }

  const lane = getLaneForResult(sheetRecord, result);
  const totalMs = getEffectiveFitnessCoordinationTotalMs(result);
  const grade = getFitnessCoordinationGrade(totalMs);

  return [
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_FITNESS_COORDINATION], style: 'detailSectionHeader' },
    buildRichKeyValueTable([
      ['Teilnehmername', participant.fullName || '-'],
      ['Prüfer', getPersonName(personNameByUserId, result?.examinerUserId || lane?.examinerUserId)],
      ['Kontrolleur', getPersonName(personNameByUserId, result?.controllerUserId || lane?.controllerUserId)],
      ['Sicherheitsrelevanter Fehler', formatSafetyIncidentLabel(result?.safetyIncident)],
      ['Bearbeitungsschutz', buildProtectionLines(result, personNameByUserId)],
    ]),
    buildTable(
      ['Zeitwertung', 'Zeit'],
      [
        [{ text: '1. Zwischenzeit', style: 'valueCell' }, { text: Number(result.firstIntermediateMs || 0) > 0 ? formatDuration(result.firstIntermediateMs) : '-', style: 'valueCell' }],
        [{ text: '2. Zwischenzeit', style: 'valueCell' }, { text: Number(result.secondIntermediateMs || 0) > 0 ? formatDuration(result.secondIntermediateMs) : '-', style: 'valueCell' }],
        [{ text: 'Gesamtzeit', style: 'valueCell' }, { text: totalMs > 0 ? formatDuration(totalMs) : '-', style: 'valueCell' }],
        [{ text: 'App-Zeit', style: 'valueCell' }, { text: Number(result.totalMs || 0) > 0 ? formatDuration(result.totalMs) : '-', style: 'valueCell' }],
        [{ text: 'Kontrollzeit', style: 'valueCell' }, { text: Number(result.controllerReferenceMs || 0) > 0 ? formatDuration(result.controllerReferenceMs) : '-', style: 'valueCell' }],
      ],
      [200, '*'],
    ),
    buildTable(
      ['Bewertung', 'Wert'],
      [[{ text: 'Note', style: 'valueCell' }, { text: getSrhtExamGradeLabelFromPoints(getGradePoints(grade)), style: 'valueCell' }]],
      [200, '*'],
    ),
  ];
}

function buildAktivrettungDetail(participant, model, personNameByUserId) {
  const sheetRecord = model?.practicalSheets?.[SRHT_EXAM_STATION_AKTIVRETTUNG] || null;
  const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(participant.candidateId)] || null;
  if (!result) {
    return [
      { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_AKTIVRETTUNG], style: 'detailSectionHeader' },
      { text: 'Kein Ergebnis erfasst.', style: 'mutedText', margin: [0, 0, 0, 10] },
    ];
  }

  const lane = getLaneForResult(sheetRecord, result);
  const sectionSum = AKTIVRETTUNG_SECTION_DEFINITIONS.reduce((sum, section) => sum + Number(result?.sectionScores?.[section.id] || 0), 0);
  const timePoints = result?.totalMs ? AKTIVRETTUNG_TIME_POINT_RULES.find((rule) => Number(result.totalMs) <= rule.maxMs)?.points || 0 : 0;
  const overallPoints = getAktivrettungOverallPoints(result);
  const grade = getAktivrettungGrade(overallPoints);

  return [
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_AKTIVRETTUNG], style: 'detailSectionHeader' },
    buildRichKeyValueTable([
      ['Teilnehmername', participant.fullName || '-'],
      ['Prüfer', getPersonName(personNameByUserId, result?.examinerUserId || lane?.examinerUserId)],
      ['Kontrolleur', getPersonName(personNameByUserId, result?.controllerUserId || lane?.controllerUserId)],
      ['Sicherheitsrelevanter Fehler', formatSafetyIncidentLabel(result?.safetyIncident)],
      ['Bearbeitungsschutz', buildProtectionLines(result, personNameByUserId)],
    ]),
    buildTable(
      ['Teilabschnitt', 'Zwischenzeit', 'Punkte'],
      AKTIVRETTUNG_SECTION_DEFINITIONS.map((section) => ([
        { text: `${section.number}. ${section.title}`, style: 'valueCell' },
        { text: section.capturesTimestamp && Number(result?.sectionTimes?.[section.id] || 0) > 0 ? formatDuration(result.sectionTimes[section.id]) : '-', style: 'valueCell' },
        { text: result?.sectionScores?.[section.id] === '' || typeof result?.sectionScores?.[section.id] === 'undefined' ? '-' : String(result.sectionScores[section.id]), style: 'valueCell' },
      ])),
      [260, 90, 60],
    ),
    buildTable(
      ['Auswertung', 'Wert'],
      [
        [{ text: 'Gesamtzeit', style: 'valueCell' }, { text: result?.totalMs ? formatDuration(result.totalMs) : '-', style: 'valueCell' }],
        [{ text: 'Σ Teilabschnitte 1-8', style: 'valueCell' }, { text: `${sectionSum} / ${AKTIVRETTUNG_SECTION_MAX_POINTS}`, style: 'valueCell' }],
        [{ text: 'P Zeit', style: 'valueCell' }, { text: `${timePoints} / ${AKTIVRETTUNG_TIME_MAX_POINTS}`, style: 'valueCell' }],
        [{ text: 'Gesamtpunkte', style: 'valueCell' }, { text: `${formatNumber(overallPoints)} / ${AKTIVRETTUNG_MAX_POINTS}`, style: 'valueCell' }],
        [{ text: 'Note', style: 'valueCell' }, { text: getSrhtExamGradeLabelFromPoints(getGradePoints(grade)), style: 'valueCell' }],
      ],
      [200, '*'],
    ),
  ];
}

function buildGroupDetail(participant, model, personNameByUserId, candidateNameById, stationCode) {
  const sheetRecord = model?.practicalSheets?.[stationCode] || null;
  const { result, group, slot } = resolveGroupContext(sheetRecord, participant.candidateId);
  if (!result || !group) {
    return [
      { text: PRACTICAL_STATION_TITLES[stationCode], style: 'detailSectionHeader' },
      { text: 'Kein Ergebnis erfasst.', style: 'mutedText', margin: [0, 0, 0, 10] },
    ];
  }

  const totalMs = getGruppenpruefungAverageTimeMs(group);
  const timePoints = getGroupPracticalTimePoints(totalMs, stationCode);
  const overallPoints = getGroupPracticalOverallPoints(result, group, stationCode);
  const grade = getGroupPracticalGrade(overallPoints, stationCode);
  const actionScoresByAction = getGruppenpruefungActionScoresByAction(result, group);
  const actionCatalogById = new Map((group?.actionCatalog || []).map((action) => [action.id, action]));
  const contributions = (group?.contributionEntries || []).filter((entry) => String(entry?.participantCandidateId || '') === String(participant.candidateId));

  return [
    { text: PRACTICAL_STATION_TITLES[stationCode], style: 'detailSectionHeader' },
    buildRichKeyValueTable([
      ['Teilnehmername', participant.fullName || '-'],
      ['Position', getGroupPositionLabel(slot?.positionId, slot?.positionLabel)],
      ['Hauptprüfer', getPersonName(personNameByUserId, slot?.primaryExaminerUserId)],
      ['Gruppenführer', getPersonName(personNameByUserId, group?.leaderExaminerUserId)],
      ['Sicherheitsrelevanter Fehler', formatSafetyIncidentLabel(group?.safetyIncident)],
      ['Bearbeitungsschutz', buildProtectionLines(result, personNameByUserId, [group?.timeLock?.isFinal ? 'Gruppenzeit bestätigt: Ja' : 'Gruppenzeit bestätigt: Nein'])],
    ]),
    { text: 'Gruppenbesetzung', style: 'subHeader' },
    buildTable(
      ['Position', 'Teilnehmer', 'Hauptprüfer', 'o.W.'],
      (group?.participants || []).map((groupParticipant) => ([
        { text: getGroupPositionLabel(groupParticipant?.positionId, groupParticipant?.positionLabel), style: 'valueCell' },
        { text: candidateNameById.get(String(groupParticipant?.candidateId || '')) || '-', style: 'valueCell' },
        { text: getPersonName(personNameByUserId, groupParticipant?.primaryExaminerUserId), style: 'valueCell' },
        { text: groupParticipant?.outOfCompetition ? 'Ja' : 'Nein', style: 'valueCell' },
      ])),
      [90, '*', 120, 40],
    ),
    { text: 'Zeitwertung der Gruppe', style: 'subHeader' },
    buildTable(
      ['Zeitpunkt', 'Wert'],
      [
        [{ text: 'Gesamtzeit', style: 'valueCell' }, { text: totalMs ? formatDuration(totalMs) : '-', style: 'valueCell' }],
        [{ text: 'P Zeit', style: 'valueCell' }, { text: `${timePoints} / 20`, style: 'valueCell' }],
      ],
      [200, '*'],
    ),
    { text: 'Zwischenbewertungen', style: 'subHeader' },
    buildTable(
      ['Prüfer', 'Handlung', 'Punkte', 'Hinweis'],
      contributions.length > 0
        ? contributions.map((entry) => ([
          { text: getPersonName(personNameByUserId, entry?.examinerUserId), style: 'valueCell' },
          { text: actionCatalogById.get(entry?.actionId)?.label || entry?.actionId || '-', style: 'valueCell' },
          { text: String(entry?.points ?? '-'), style: 'valueCell' },
          { text: entry?.notes || '-', style: 'valueCell' },
        ]))
        : [[
          { text: '-', style: 'valueCell' },
          { text: 'Keine Zwischenbewertungen erfasst.', style: 'valueCell' },
          { text: '-', style: 'valueCell' },
          { text: '-', style: 'valueCell' },
        ]],
      [120, '*', 60, 90],
    ),
    buildTable(
      ['Punkteermittlung', 'Wert'],
      [
        ...Object.entries(actionScoresByAction).map(([actionId, points]) => ([
          { text: actionCatalogById.get(actionId)?.label || actionId, style: 'valueCell' },
          { text: String(points), style: 'valueCell' },
        ])),
        [{ text: 'Mittel Handlung', style: 'valueCell' }, { text: getGruppenpruefungActionAverage(result, group) ?? '-', style: 'valueCell' }],
        [{ text: 'P Zeit', style: 'valueCell' }, { text: `${timePoints} / 20`, style: 'valueCell' }],
        [{ text: 'Gesamtpunkte', style: 'valueCell' }, { text: `${formatNumber(overallPoints)} / 40`, style: 'valueCell' }],
        [{ text: 'Note', style: 'valueCell' }, { text: getSrhtExamGradeLabelFromPoints(getGradePoints(grade)), style: 'valueCell' }],
      ],
      [220, '*'],
    ),
  ];
}

function buildEinzelDetail(participant, model, personNameByUserId) {
  const sheetRecord = model?.practicalSheets?.[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG] || null;
  const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(participant.candidateId)] || null;
  if (!result) {
    return [
      { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG], style: 'detailSectionHeader' },
      { text: 'Kein Ergebnis erfasst.', style: 'mutedText', margin: [0, 0, 0, 10] },
    ];
  }

  const lane = getLaneForResult(sheetRecord, result);
  const sectionSum = EINZELUEBERPRUEFUNG_SECTION_DEFINITIONS.reduce((sum, section) => sum + Number(result?.sectionScores?.[section.id] || 0), 0);
  const timePoints = result?.totalMs ? EINZELUEBERPRUEFUNG_TIME_POINT_RULES.find((rule) => Number(result.totalMs) <= rule.maxMs)?.points || 0 : 0;
  const overallPoints = getEinzelueberpruefungOverallPoints(result);
  const grade = getEinzelueberpruefungGrade(overallPoints);

  return [
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG], style: 'detailSectionHeader' },
    buildRichKeyValueTable([
      ['Teilnehmername', participant.fullName || '-'],
      ['Prüfer', getPersonName(personNameByUserId, result?.examinerUserId || lane?.examinerUserId)],
      ['Kontrolleur', getPersonName(personNameByUserId, result?.controllerUserId || lane?.controllerUserId)],
      ['Sicherheitsrelevanter Fehler', formatSafetyIncidentLabel(result?.safetyIncident)],
      ['Bearbeitungsschutz', buildProtectionLines(result, personNameByUserId)],
    ]),
    buildTable(
      ['Teilabschnitt', 'Zwischenzeit', 'Punkte'],
      EINZELUEBERPRUEFUNG_SECTION_DEFINITIONS.map((section) => ([
        { text: `${section.number}. ${section.title}`, style: 'valueCell' },
        { text: '-', style: 'valueCell' },
        { text: result?.sectionScores?.[section.id] === '' || typeof result?.sectionScores?.[section.id] === 'undefined' ? '-' : String(result.sectionScores[section.id]), style: 'valueCell' },
      ])),
      [260, 90, 60],
    ),
    buildTable(
      ['Auswertung', 'Wert'],
      [
        [{ text: 'Gesamtzeit', style: 'valueCell' }, { text: result?.totalMs ? formatDuration(result.totalMs) : '-', style: 'valueCell' }],
        [{ text: 'Σ Teilabschnitte 1-11', style: 'valueCell' }, { text: `${sectionSum} / ${EINZELUEBERPRUEFUNG_SECTION_MAX_POINTS}`, style: 'valueCell' }],
        [{ text: 'P Zeit', style: 'valueCell' }, { text: `${timePoints} / 10`, style: 'valueCell' }],
        [{ text: 'Gesamtpunkte', style: 'valueCell' }, { text: `${formatNumber(overallPoints)} / 120`, style: 'valueCell' }],
        [{ text: 'Note', style: 'valueCell' }, { text: getSrhtExamGradeLabelFromPoints(getGradePoints(grade)), style: 'valueCell' }],
      ],
      [200, '*'],
    ),
  ];
}

export function buildSrhtExamOverallReportContent({ model, brandingAssets = {} }) {
  const participantCount = Array.isArray(model?.participants) ? model.participants.length : 0;
  const personNameByUserId = buildPersonNameByUserId(model);
  const candidateNameById = buildCandidateNameById(model);
  const reportEvaluation = buildReportEvaluation(model);
  const reportByCandidateId = new Map(reportEvaluation.map((candidate) => [String(candidate.candidateId), candidate]));
  const participants = (model?.participants || []).map((participant) => ({
    ...participant,
    reportSummary: reportByCandidateId.get(String(participant.candidateId)) || null,
  }));
  const passedCount = reportEvaluation.filter((candidate) => candidate.resultStatus === 'passed').length;
  const failedCount = reportEvaluation.filter((candidate) => candidate.resultStatus === 'failed').length;
  const openCount = reportEvaluation.filter((candidate) => candidate.resultStatus === 'open').length;

  const content = [
    {
      columns: [
        brandingAssets.feuerwehrLogo ? { image: brandingAssets.feuerwehrLogo, width: 72 } : { text: '' },
        {
          width: '*',
          stack: [
            { text: 'Prüfung SRHT', style: 'title' },
            { text: model?.exportLabel || 'Gesamtbericht Prüfung', style: 'subtitle' },
            { text: `Erstellt am ${formatDateTime(model?.generatedAt)} für ${participantCount} Teilnehmer`, style: 'meta' },
          ],
          alignment: 'center',
        },
        brandingAssets.hoehenrettungLogo ? { image: brandingAssets.hoehenrettungLogo, width: 54, alignment: 'right' } : { text: '' },
      ],
      margin: [0, 0, 0, 16],
    },
    { text: 'Übersicht', style: 'sectionHeader' },
    buildRichKeyValueTable([
      ['Prüfung', model?.exam?.label || '-'],
      ['Lehrgangszeitraum', getSourceCourseRangeLabel(model?.sourceCourses)],
      ['Schriftliche Prüfung', formatDate(model?.exam?.writtenExamDate)],
      ['Praktische Prüfung', formatDate(model?.exam?.practicalExamDate)],
      ['Ort', model?.exam?.location || '-'],
      ['Teilnehmer', `${participantCount} (${passedCount} bestanden, ${failedCount} durchgefallen, ${openCount} offen)`],
      ['Prüfungsvorsitz', model?.staff?.chair?.fullName || '-'],
      ['Lehrgangsleitung', model?.staff?.courseLeader?.fullName || '-'],
      ['Anwesende Prüfer', formatStaffList(model?.staff?.examiners)],
      ['Personalrat', formatStaffList(model?.staff?.personalrat)],
      ['Abteilung 37/4', formatStaffList(model?.staff?.department37_4)],
      ['Gleichstellungsvertretung', formatStaffList(model?.staff?.equalOpportunityOfficers)],
      ['Weitere Beobachter', formatStaffList(model?.staff?.observers, { includeNotes: true })],
    ]),
    { text: 'Information', style: 'sectionHeader' },
    { text: getReportInfoText(model?.exam), style: 'valueText', margin: [0, 0, 0, 14] },
    { text: 'Prüfungsergebnisse', style: 'sectionHeader' },
    { text: 'Prüfungsteile', style: 'subHeader' },
    { text: 'Schriftliche Prüfung', style: 'detailSectionHeader' },
    buildTable(['Teilnehmername', 'Erreichte Punkte / Maximalpunkte', 'Note'], buildWrittenRows(participants), ['*', 160, 90]),
    { text: 'Praktische Prüfung', style: 'detailSectionHeader' },
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_FITNESS_COORDINATION], style: 'detailSubHeader' },
    buildTable(['Teilnehmername', 'Erreichte Zeit(en)', 'Sicherheitsrelevanter Fehler', 'Note', 'Prüfer'], buildFitnessRows(model, participants, personNameByUserId), [100, 92, 118, 54, '*']),
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_AKTIVRETTUNG], style: 'detailSubHeader' },
    buildTable(['Teilnehmername', 'Erreichte Zeit(en)', 'Sicherheitsrelevanter Fehler', 'Erreichte Punkte / Maximalpunkte', 'Note', 'Prüfer'], buildAktivrettungRows(model, participants, personNameByUserId), [90, 82, 86, 96, 48, '*']),
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_FALLSCHUTZ], style: 'detailSubHeader' },
    buildTable(['Teilnehmername', 'Erreichte Zeit(en)', 'Sicherheitsrelevanter Fehler', 'Erreichte Punkte / Maximalpunkte', 'Note', 'Prüfer'], buildGroupRows(model, participants, personNameByUserId, SRHT_EXAM_STATION_FALLSCHUTZ), [90, 82, 86, 96, 48, '*']),
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_GRUPPENPRUEFUNG], style: 'detailSubHeader' },
    buildTable(['Teilnehmername', 'Erreichte Zeit(en)', 'Sicherheitsrelevanter Fehler', 'Erreichte Punkte / Maximalpunkte', 'Note', 'Prüfer'], buildGroupRows(model, participants, personNameByUserId, SRHT_EXAM_STATION_GRUPPENPRUEFUNG), [90, 82, 86, 96, 48, '*']),
    { text: PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG], style: 'detailSubHeader' },
    buildTable(['Teilnehmername', 'Erreichte Zeit(en)', 'Sicherheitsrelevanter Fehler', 'Erreichte Punkte / Maximalpunkte', 'Note', 'Prüfer'], buildEinzelRows(model, participants, personNameByUserId), [90, 82, 86, 96, 48, '*']),
    { text: 'Gesamtranking und Endnotenbildung', style: 'sectionHeader' },
  ];

  reportEvaluation.forEach((candidate) => {
    const badgeConfig = getResultBadgeConfig(candidate.resultStatus);
    const oralRecord = candidate?.oralRetake?.record || null;
    content.push({
      table: {
        widths: ['*'],
        body: [[{
          stack: [
            {
              columns: [
                {
                  width: '*',
                  stack: [
                    { text: `Rang ${candidate.rankPosition}: ${candidate.fullName}`, style: 'participantHeader', margin: [0, 0, 0, 2] },
                    { text: candidate.resultStatus === 'open' ? 'Abschlussauswertung noch nicht vollständig' : `${formatNumber(candidate.percentScore)}% · ${formatNumber(candidate.overallPointsRaw)} / 15 Punkte · ${candidate.reportGradeLabel}`, style: 'mutedText' },
                  ],
                },
                {
                  width: 115,
                  stack: [
                    buildBadge(badgeConfig.label, badgeConfig.fillColor, badgeConfig.textColor),
                    oralRecord
                      ? buildBadge(
                        oralRecord.isDraft ? 'Mündlich vorbereitet' : `Mündlich: ${oralRecord.outcome === 'passed' ? 'Bestanden' : 'Nicht bestanden'}`,
                        oralRecord.isDraft ? '#F59E0B' : (oralRecord.outcome === 'passed' ? '#DDEFE0' : '#FDE8E8'),
                        oralRecord.isDraft ? '#111827' : (oralRecord.outcome === 'passed' ? '#1B5E20' : '#7F1D1D'),
                      )
                      : { text: '' },
                  ],
                  alignment: 'right',
                },
              ],
              margin: [0, 0, 0, 8],
            },
            {
              columns: [
                { width: '33%', stack: [buildSummaryTile(candidate.disciplines[0]), buildSummaryTile(candidate.disciplines[3])], margin: [0, 0, 4, 0] },
                { width: '33%', stack: [buildSummaryTile(candidate.disciplines[1]), buildSummaryTile(candidate.disciplines[4])], margin: [4, 0, 4, 0] },
                { width: '34%', stack: [buildSummaryTile(candidate.disciplines[2]), buildSummaryTile(candidate.disciplines[5])], margin: [4, 0, 0, 0] },
              ],
              margin: [0, 0, 0, 10],
            },
            { text: 'Herleitung der Endnote', style: 'detailSubHeader' },
            buildTable(
              ['Schritt', 'Wert'],
              [
                [{ text: 'Schriftliche Prüfung', style: 'valueCell' }, { text: `${candidate.writtenDiscipline.displayPoints} / 15 Punkte`, style: 'valueCell' }],
                [{ text: 'Durchschnitt praktische Prüfung', style: 'valueCell' }, { text: `${formatNumber(candidate.practicalAverage)} / 15 Punkte`, style: 'valueCell' }],
                [{ text: 'Praktische Prüfung × 2', style: 'valueCell' }, { text: formatNumber(candidate.weightedPractical), style: 'valueCell' }],
                [{ text: '(Schriftlich + Praktisch × 2) ÷ 3', style: 'valueCell' }, { text: `${formatNumber(candidate.overallPointsRaw)} / 15 Punkte`, style: 'valueCell' }],
                [{ text: 'Endnote', style: 'valueCell' }, { text: candidate.reportGradeLabel, style: 'valueCell' }],
              ],
              [220, '*'],
            ),
            {
              table: {
                widths: ['33%', '33%', '34%'],
                body: [[
                  buildSummaryPill(`Beste Disziplin: ${candidate.bestDiscipline?.title || '-'}`, '#F8FAFC'),
                  buildSummaryPill(`Schwächste Disziplin: ${candidate.weakestDiscipline?.title || '-'}`, '#FDECEC'),
                  buildSummaryPill(`Gesamt: ${candidate.overallWholePoints} Punkte`, '#FEF3C7'),
                ]],
              },
              layout: 'noBorders',
            },
          ],
          margin: [10, 10, 10, 10],
        }]],
      },
      layout: {
        hLineColor: () => '#D6DEE6',
        vLineColor: () => '#D6DEE6',
        hLineWidth: () => 1,
        vLineWidth: () => 1,
      },
      margin: [0, 0, 0, 12],
    });
  });

  content.push(
    { text: 'Bewertungsschlüssel', style: 'sectionHeader' },
    ...buildRatingKeyBlock(PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_FITNESS_COORDINATION], FITNESS_GRADE_RULES, [], 'Maßgeblich ist die bessere bzw. freigegebene Gesamtzeit aus App-Zeit und Kontrollzeit.'),
    ...buildRatingKeyBlock(PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_AKTIVRETTUNG], AKTIVRETTUNG_GRADE_RANGES, AKTIVRETTUNG_TIME_POINT_RULES),
    ...buildRatingKeyBlock(PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_FALLSCHUTZ], FALLSCHUTZ_GRADE_RANGES, FALLSCHUTZ_TIME_POINT_RULES),
    ...buildRatingKeyBlock(PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_GRUPPENPRUEFUNG], GRUPPENPRUEFUNG_GRADE_RANGES, GRUPPENPRUEFUNG_TIME_POINT_RULES),
    ...buildRatingKeyBlock(PRACTICAL_STATION_TITLES[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG], EINZELUEBERPRUEFUNG_GRADE_RANGES, EINZELUEBERPRUEFUNG_TIME_POINT_RULES),
    { text: 'Prüfergebnisse nach Teilnehmern', style: 'sectionHeader' },
  );

  participants.forEach((participant) => {
    content.push({ text: participant.fullName || '-', style: 'participantHeader', pageBreak: 'before', margin: [0, 0, 0, 8] });
    content.push(...buildWrittenDetail(participant, personNameByUserId));
    content.push(...buildFitnessDetail(participant, model, personNameByUserId));
    content.push(...buildAktivrettungDetail(participant, model, personNameByUserId));
    content.push(...buildGroupDetail(participant, model, personNameByUserId, candidateNameById, SRHT_EXAM_STATION_FALLSCHUTZ));
    content.push(...buildGroupDetail(participant, model, personNameByUserId, candidateNameById, SRHT_EXAM_STATION_GRUPPENPRUEFUNG));
    content.push(...buildEinzelDetail(participant, model, personNameByUserId));
  });

  return content;
}